import React, { FC, ReactNode } from 'react';
import { Accept, DropzoneOptions, useDropzone } from 'react-dropzone';
import { UploadBoxStyled } from '@components/UploadModal/core/DropZoneBlock/DropZoneBlock.styled';
import { Box, Button, SxProps, Typography } from '@mui/material';
import {
  CloudDisabledIcon,
  CloudIcon,
  DocumentDisabledIcon,
  DocumentIcon,
} from '@utils/iconsDefs';

type Props = {
  isDisabled: boolean;
  options?: Partial<DropzoneOptions>;
  title?: string;
  description?: string;
  sx?: SxProps;
  accept?: Accept;
  actions?: ReactNode;
};

const DefaultAccept: Accept = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
};

const DropZoneBlock: FC<Props> = ({
  isDisabled,
  options,
  title = 'Choose a file or drag and drop it here',
  description = '.xlsx format, up to 2 MB',
  sx,
  accept = DefaultAccept,
  actions,
}) => {
  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    noClick: true,
    multiple: false,
    disabled: isDisabled,
    accept,
    ...options,
  });

  return (
    <UploadBoxStyled {...getRootProps()} sx={sx}>
      <input {...getInputProps()} />

      {isDisabled ? <CloudDisabledIcon /> : <CloudIcon />}

      <Typography
        variant="h4"
        sx={{ mt: 2 }}
        color={isDisabled ? 'grey.700' : 'grey.1000'}
      >
        {title}
      </Typography>

      <Typography
        variant="body1"
        sx={{ mt: 2 }}
        color={isDisabled ? 'grey.700' : 'grey.800'}
      >
        {description}
      </Typography>

      <Button
        onClick={openFileDialog}
        startIcon={isDisabled ? <DocumentDisabledIcon /> : <DocumentIcon />}
        variant="outlined"
        disabled={isDisabled}
        sx={{ mt: 6, width: 230, height: 40 }}
      >
        Choose a file
      </Button>

      {actions && <Box sx={{ mt: 6 }}>{actions}</Box>}
    </UploadBoxStyled>
  );
};

export default DropZoneBlock;
